// Linea SIP real con JsSIP contra la pasarela de voz Xmartek (Asterisk).
// Misma configuracion que ya funciono en /s/3cx/telefono: dominio xmartek.voz, sin STUN,
// audio remoto enganchado desde la conexion WebRTC (en salientes la conexion existe antes del evento).

import type * as JsSIPTipos from 'jssip';
import type { RTCSession } from 'jssip/lib/RTCSession';
import type { UA } from 'jssip/lib/UA';

import { dialable } from '../lib/phone-number';
import { loadConfig } from './config';
import { Emitter, type CallHandle, type EndInfo, type Line, type LineConfig, type LineStatus } from './line';

const PC_CONFIG = { iceServers: [] as RTCIceServer[] };

// Microfonos conectados (se refresca al conectar y cuando el sistema avisa de un cambio). null = aun no se sabe.
let microfonos: string[] | null = null;
function refrescarMicrofonos() {
  const md = typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined;
  if (!md?.enumerateDevices) return;
  void md
    .enumerateDevices()
    .then((ds) => (microfonos = ds.filter((d) => d.kind === 'audioinput').map((d) => d.deviceId)))
    .catch(() => {});
}
let escuchandoCambios = false;
function vigilarDispositivos() {
  refrescarMicrofonos();
  const md = typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined;
  if (escuchandoCambios || !md?.addEventListener) return;
  escuchandoCambios = true;
  md.addEventListener('devicechange', refrescarMicrofonos);
}

// Restricciones de audio con el microfono elegido en la prueba de audio. Si ese microfono ya no esta conectado
// se usa el predeterminado (exact fallaria la llamada); si aun no sabemos que hay conectado, se pide como preferido.
export function audioPedido(id: string, conectados: string[] | null): MediaTrackConstraints | true {
  if (!id) return true;
  if (conectados === null) return { deviceId: { ideal: id } };
  return conectados.includes(id) ? { deviceId: { exact: id } } : true;
}

const media = () => ({ mediaConstraints: { audio: audioPedido(loadConfig().microfono, microfonos), video: false }, pcConfig: PC_CONFIG });

const audioEl = () => document.getElementById('audio-remoto') as HTMLAudioElement | null;

// Saca el audio de las llamadas por los altavoces elegidos (Chrome y Edge; en otros navegadores sale por el del sistema).
export function aplicarAltavoz(id = loadConfig().altavoz) {
  const el = audioEl() as (HTMLAudioElement & { setSinkId?: (id: string) => Promise<void>; sinkId?: string }) | null;
  if (!el || typeof el.setSinkId !== 'function' || el.sinkId === id) return;
  void el.setSinkId(id).catch(() => {
    // Ese altavoz ya no esta: volver al predeterminado.
    void el.setSinkId?.('').catch(() => {});
  });
}

function streamOf(pc: RTCPeerConnection | undefined) {
  if (!pc) return null;
  const tracks = pc.getReceivers().map((r) => r.track).filter(Boolean) as MediaStreamTrack[];
  return tracks.length ? new MediaStream(tracks) : null;
}

let seq = 0;

class SipCall implements CallHandle {
  readonly id = `sip-${++seq}`;
  readonly direction: 'in' | 'out';
  readonly remote: string;
  readonly remoteName?: string;
  private ev = new Emitter<'progress' | 'accepted' | 'ended' | 'failed'>();
  private finished = false;

  constructor(
    readonly session: RTCSession,
    direction: 'in' | 'out',
    private readonly domain: string,
    private readonly onActive: (c: SipCall) => void,
  ) {
    this.direction = direction;
    this.remote = session.remote_identity?.uri?.user ?? '';
    this.remoteName = session.remote_identity?.display_name || undefined;

    const hook = (pc?: RTCPeerConnection) => {
      if (!pc || (pc as RTCPeerConnection & { _xmk?: boolean })._xmk) return;
      (pc as RTCPeerConnection & { _xmk?: boolean })._xmk = true;
      pc.addEventListener('track', () => this.onActive(this));
    };
    hook(session.connection);
    session.on('peerconnection', (e: { peerconnection: RTCPeerConnection }) => hook(e.peerconnection));
    session.on('progress', () => this.ev.emit('progress'));
    session.on('accepted', () => {
      this.onActive(this);
      this.ev.emit('accepted');
    });
    session.on('confirmed', () => this.onActive(this));
    const end = (kind: 'ended' | 'failed') => (e: { cause?: string; originator?: string }) => {
      if (this.finished) return;
      this.finished = true;
      const info: EndInfo = { cause: String(e?.cause ?? ''), byRemote: e?.originator === 'remote' };
      this.ev.emit(kind, info);
    };
    session.on('ended', end('ended'));
    session.on('failed', end('failed'));
  }

  on(event: 'progress' | 'accepted' | 'ended' | 'failed', cb: ((info: EndInfo) => void) | (() => void)) {
    this.ev.on(event, cb as () => void);
  }

  answer() {
    this.session.answer(media());
  }

  reject(codigo: 480 | 486 = 486) {
    try {
      this.session.terminate(codigo === 480 ? { status_code: 480, reason_phrase: 'Temporarily Unavailable' } : { status_code: 486, reason_phrase: 'Busy Here' });
    } catch {
      /* ya termino */
    }
  }

  hangup() {
    try {
      this.session.terminate();
    } catch {
      /* ya termino */
    }
  }

  setMuted(muted: boolean) {
    if (muted) this.session.mute({ audio: true });
    else this.session.unmute({ audio: true });
  }

  setHold(hold: boolean) {
    const local = this.session.isOnHold().local;
    if (hold && !local) this.session.hold();
    if (!hold && local) this.session.unhold();
  }

  sendDtmf(tone: string) {
    try {
      // RFC 4733 por el canal de audio: es lo que la pasarela (dtmf_mode por defecto) entiende.
      this.session.sendDTMF(tone, { transportType: 'RFC2833' } as never);
    } catch {
      this.session.sendDTMF(tone);
    }
  }

  transferBlind(target: string) {
    return new Promise<void>((resolve, reject) => {
      const destino = dialable(target);
      if (!destino) return reject(new Error('Destino vacío'));
      this.session.refer(`sip:${destino}@${this.domain}`, {
        eventHandlers: {
          requestSucceeded: () => resolve(),
          requestFailed: (e: { response?: { status_code?: number } }) => reject(new Error(`La central rechazó la transferencia${e?.response?.status_code ? ` (${e.response.status_code})` : ''}`)),
          failed: () => reject(new Error('La transferencia no se completó')),
        },
      } as never);
    });
  }

  transferAttended(consult: CallHandle) {
    return new Promise<void>((resolve, reject) => {
      const other = (consult as SipCall).session;
      this.session.refer(other.remote_identity.uri.toString(), {
        replaces: other,
        eventHandlers: {
          requestSucceeded: () => resolve(),
          requestFailed: (e: { response?: { status_code?: number } }) => reject(new Error(`La central rechazó la transferencia${e?.response?.status_code ? ` (${e.response.status_code})` : ''}`)),
          failed: () => reject(new Error('La transferencia no se completó')),
        },
      } as never);
    });
  }

  playAudio() {
    const el = audioEl();
    const stream = streamOf(this.session.connection);
    if (el && stream) {
      aplicarAltavoz();
      el.srcObject = stream;
      void el.play().catch(() => {});
    }
  }
}

export class SipLine implements Line {
  readonly kind = 'sip' as const;
  status: LineStatus = 'sin-configurar';
  statusDetail = '';
  private ua: UA | null = null;
  // Cada conexion o desconexion cambia el turno: si JsSIP termina de bajar despues de un "desconectar", no se conecta.
  private turno = 0;
  private domain = 'xmartek.voz';
  private ev = new Emitter<'status' | 'incoming'>();

  private set(s: LineStatus, detail = '') {
    this.status = s;
    this.statusDetail = detail;
    this.ev.emit('status', s, detail);
  }

  onStatus(cb: (s: LineStatus, detail: string) => void) {
    return this.ev.on('status', cb as never);
  }

  onIncoming(cb: (c: CallHandle) => void) {
    return this.ev.on('incoming', cb as never);
  }

  connect(cfg: LineConfig) {
    this.disconnect(true);
    if (!cfg.ws || !cfg.usuario || !cfg.clave) {
      this.set('sin-configurar', 'Falta la dirección de la pasarela, el usuario o la clave.');
      return;
    }
    this.domain = cfg.dominio || 'xmartek.voz';
    vigilarDispositivos();
    this.set('conectando', 'Conectando con la pasarela de voz…');
    const turno = this.turno;
    // JsSIP (unos 250 kB) se baja solo al conectar el telefono web, no en la primera carga de la Consola.
    import('jssip').then(
      ({ default: JsSIP }) => turno === this.turno && this.arrancar(JsSIP, cfg),
      (err: Error) => turno === this.turno && this.set('error', `No se pudo cargar el teléfono web: ${err.message}`),
    );
  }

  private arrancar(JsSIP: typeof JsSIPTipos, cfg: LineConfig) {
    try {
      const ua = new JsSIP.UA({
        sockets: [new JsSIP.WebSocketInterface(cfg.ws)],
        uri: `sip:${cfg.usuario}@${this.domain}`,
        password: cfg.clave,
        display_name: cfg.nombre || 'Consola Xmartek',
        register: true,
        session_timers: false,
        user_agent: 'Xmartek Consola',
      });
      this.ua = ua;
      ua.on('connected', () => this.set('conectando', 'Conectado a la pasarela; registrando…'));
      ua.on('disconnected', () => this.ua === ua && this.set('error', 'Sin conexión con la pasarela de voz.'));
      ua.on('registered', () => this.set('en-linea', ''));
      ua.on('unregistered', () => this.ua === ua && this.set('desconectado', 'Registro cerrado.'));
      ua.on('registrationFailed', (e: { cause?: string }) =>
        this.set('error', e?.cause === 'Authentication Error' ? 'Usuario o clave SIP incorrectos.' : `Registro fallido: ${e?.cause ?? 'sin detalle'}`),
      );
      ua.on('newRTCSession', (e: { session: RTCSession; originator: string }) => {
        if (e.originator !== 'remote') return;
        this.ev.emit('incoming', new SipCall(e.session, 'in', this.domain, (c) => c.playAudio()));
      });
      ua.start();
    } catch (err) {
      this.set('error', `Configuración inválida: ${(err as Error).message}`);
    }
  }

  disconnect(silent = false) {
    this.turno++;
    const ua = this.ua;
    this.ua = null;
    if (ua) {
      try {
        ua.stop();
      } catch {
        /* nada */
      }
    }
    if (!silent) this.set('desconectado', 'Teléfono desconectado.');
  }

  call(target: string): CallHandle {
    if (!this.ua || !this.ua.isRegistered()) throw new Error('El teléfono no está en línea.');
    const destino = dialable(target);
    if (!destino) throw new Error('Número vacío.');
    const session = this.ua.call(`sip:${destino}@${this.domain}`, media()) as RTCSession;
    return new SipCall(session, 'out', this.domain, (c) => c.playAudio());
  }
}
